"use client"
import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { PlusIcon, ArrowLeft, Building2, Search, RefreshCw } from "lucide-react"
import { Business, columns } from "./columns"
import { DataTable } from "@/components/data-table"
import BusinessService from "@/api/businesses.service"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"

async function getData(): Promise<Business[]> {
  // Fetch data from your API here.
  const response = await BusinessService.getAllBusinesses();
  if (response.data.result === 'invalid') {
    window.location.href = '/logout';
    return [];
  }
  console.log(response.data.businesses);
  return response.data.businesses.map((business: any) => ({
    id: business.id,
    publicId: business.publicId,
    businessType: business.businessType,
    name: business.name,
    description: business.description,
    address: business.address,
    city: business.city,
    country: business.country,
    emailAddress: business.emailAddress,
    phoneNumber: business.phoneNumber,
    website: business.website,
    logoImgLink: business.logoImg,
    bannerImgLink: business.bannerImg,
    dateCreated: business.dateCreated,
  }));
}

export default function Businesses() {
  const [data, setData] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  const fetchData = async () => {
    setIsLoading(true);
    const result = await getData();
    setData(result);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = searchQuery 
    ? data.filter(business => 
        business.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.emailAddress?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.country?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : data;

  return (
    <div className="container py-4 sm:py-8 mx-auto px-4 sm:px-6 max-w-7xl">
      <div className="flex flex-col space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground h-9"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Businesses</h1>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button 
              onClick={fetchData} 
              variant="outline" 
              size="sm"
              className="text-muted-foreground h-9 justify-center"
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={() => window.location.href=`/businesses/create`} 
              className="bg-gradient-to-r from-primary to-primary/90 text-white shadow-sm hover:shadow-md transition-all h-9 justify-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" /> Create Business
            </Button>
          </div>
        </div>

        <Card className="shadow-sm border-slate-200/60 overflow-hidden">
          <CardHeader className="pb-3 px-4 sm:px-6">
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <Building2 size={18} className="text-muted-foreground" />
              Business Directory
            </CardTitle>
          </CardHeader>
          <Separator />
          <CardContent className="p-4 sm:p-6">
            <div className="mb-6 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search businesses by name, email, city or country..."
                className="pl-10 h-10 bg-slate-50 border-slate-200 focus-visible:ring-primary/50"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="min-w-full inline-block align-middle">
                <DataTable 
                  columns={columns} 
                  data={filteredData} 
                  section="Businesses" 
                  isLoading={isLoading} 
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
